import Swal from "sweetalert2";
import * as Cesium from 'cesium';
import { makeStyles } from "@mui/styles";
import FormControl from '@mui/material/FormControl';
import { useDispatch, useSelector } from "react-redux";
import useViewerStore from '../../store-zustand/viewer';
import withReactContent from "sweetalert2-react-content";
import React, { useState, useRef, useEffect } from "react";
import TooltipIconButton from "components/TooltipIconButton";
import { getGlobePositionWithTerrain } from '../MapView/MapUtils';
import { MenuItem, Select, CircularProgress } from '@mui/material';
import { Content, FormItem, Indicators, Footer, Wrapper } from '../ActionModal';
import { collections_descriptions } from '../Sidebar/SidebarProjects/Collections';
import { Close, DataSaverOffOutlined, HistoryOutlined, CreditCardOutlined, CheckCircleOutline, CloudDownloadOutlined, CropOutlined } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: 0,
    minWidth: 131,
    position: 'relative'
  }
}));

const projections = [
  { value: 1, label: 'Source' },
  { value: 2, label: 'EPSG:4326' },
  { value: 3, label: 'EPSG:3158' },
  { value: 4, label: 'EPSG:3857' }
];

const DataDownloadModal = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({ catalog: 2, projection: 1 });
  const notifyDownload = useSelector(state => state.asset.notifyDownload);
  const downloadStarted = useSelector(state => state.asset.downloadStarted);
  const currentCollection = useSelector(state => state.asset.currentCollection);
  const downloadPolygonAdded = useSelector(state => state.asset.downloadPolygonAdded);
  const totalSize = useSelector(state => state.asset.selectedAssetsTotalSize);
  const mouseHandler = useRef();
  const rectanglePositions = useRef([]);
  const rectangleAdded = useRef(false);
  const notifyDownloadRef = useRef(notifyDownload);
  const { setmapboxvisibility } = useViewerStore();
  
  const collections = window.demonstrable_collections.map((collection, index) => {
    const collection_description = collections_descriptions[collection.name];
    if (collection_description) return { value: index, title: collection.name, text: collection_description.text, icon: collection_description.icon }
    return null;
  }).filter(collection => collection !== null);
  
  collections.sort((a, b) => {
    const indexA = Object.keys(collections_descriptions).indexOf(a.title);
    const indexB = Object.keys(collections_descriptions).indexOf(b.title);
    return (indexA === -1 ? 1000 : indexA) - (indexB === -1 ? 1000 : indexB);
  });

  const handleChange = (event, name) => {
    const selectedCollection = collections.find(item => item.value === event.target.value);
    setFormData({ ...formData, [name]: event.target.value });
    dispatch({ type: "SET_CURRENT_COLLECTION", currentCollection: selectedCollection });
  };

  const closeModal = () => {
    if (mouseHandler.current) {
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }

    if (window.itemsPolygon) {
      window.MAIN_CESIUM_VIEWER.entities.remove(window.itemsPolygon);
      delete (window.itemsPolygon);
    }
    rectanglePositions.current = [];
    rectangleAdded.current = false;

    document.getElementById("cesium-wrapper").style.cursor = 'auto';
    dispatch({ type: 'SET_DOWNLOAD_POLYGON', downloadPolygonAdded: false });
    dispatch({ type: 'TOGGLE_DATA_DOWNLOAD_MODAL', show: false });
    setmapboxvisibility(0);
  }

  const downloadSelection = () => {
    if (totalSize === 0) return;
    
    withReactContent(Swal).fire({
      icon: 'info',
      title: 'Info',
      html: 'Please contact sales with a list of your desired items for download: <a href="mailto:sales@fastmap.ca">sales@fastmap.ca</a>',
    });
  }

  const drawArea = () => {
    document.getElementById("cesium-wrapper").style.cursor = 'crosshair';
    if (mouseHandler.current) return
    mouseHandler.current = new Cesium.ScreenSpaceEventHandler(window.MAIN_CESIUM_VIEWER.scene.canvas);

    mouseHandler.current.setInputAction(function (movement) {
      let newPos = getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, movement.endPosition);
      if (rectanglePositions.current[0] && !rectangleAdded.current)
        rectanglePositions.current[1] = newPos;
      if (rectanglePositions.current.length === 2 && !rectangleAdded.current) {
        if (window.itemsPolygon) return
        window.itemsPolygon = window.MAIN_CESIUM_VIEWER.entities.add({
          rectangle: {
            coordinates: new Cesium.CallbackProperty(() => {
              if (rectanglePositions.current[0].longitude < rectanglePositions.current[1].longitude) {
                var west = rectanglePositions.current[0].longitude;
                var east = rectanglePositions.current[1].longitude;
              } else {
                var west = rectanglePositions.current[1].longitude;
                var east = rectanglePositions.current[0].longitude;
              }

              if (rectanglePositions.current[0].latitude < rectanglePositions.current[1].latitude) {
                var south = rectanglePositions.current[0].latitude;
                var north = rectanglePositions.current[1].latitude;
              } else {
                var south = rectanglePositions.current[1].latitude;
                var north = rectanglePositions.current[0].latitude;
              }

              return Cesium.Rectangle.fromRadians(west, south, east, north);
            }, false),
            material: new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
          },
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              if (rectanglePositions.current[0].longitude < rectanglePositions.current[1].longitude) {
                var west = rectanglePositions.current[0].longitude;
                var east = rectanglePositions.current[1].longitude;
              } else {
                var west = rectanglePositions.current[1].longitude;
                var east = rectanglePositions.current[0].longitude;
              }

              if (rectanglePositions.current[0].latitude < rectanglePositions.current[1].latitude) {
                var south = rectanglePositions.current[0].latitude;
                var north = rectanglePositions.current[1].latitude;
              } else {
                var south = rectanglePositions.current[1].latitude;
                var north = rectanglePositions.current[0].latitude;
              }

              var coords = [west, south, east, south, east, north, west, north, west, south];

              return new Cesium.Cartesian3.fromRadiansArray(coords);
            }, false),
            width: 1.5,
            material: new Cesium.Color.fromCssColorString('#487BF2'),
            clampToGround: true
          }
        });
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    mouseHandler.current.setInputAction(function (click) {
      if (rectanglePositions.current.length === 0) {
        rectanglePositions.current.push(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position));
      } else {
        if (!rectangleAdded.current) {
          if (rectanglePositions.current[0].longitude < rectanglePositions.current[1].longitude) {
            var west = rectanglePositions.current[0].longitude;
            var east = rectanglePositions.current[1].longitude;
          } else {
            var west = rectanglePositions.current[1].longitude;
            var east = rectanglePositions.current[0].longitude;
          }

          if (rectanglePositions.current[0].latitude < rectanglePositions.current[1].latitude) {
            var south = rectanglePositions.current[0].latitude;
            var north = rectanglePositions.current[1].latitude;
          } else {
            var south = rectanglePositions.current[1].latitude;
            var north = rectanglePositions.current[0].latitude;
          }

          var polylineCoords = [west, south, east, south, east, north, west, north, west, south];

          window.MAIN_CESIUM_VIEWER.entities.remove(window.itemsPolygon);
          window.itemsPolygon = window.MAIN_CESIUM_VIEWER.entities.add({
            rectangle: {
              coordinates: Cesium.Rectangle.fromRadians(west, south, east, north),
              material: new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
            },
            polyline: {
              positions: Cesium.Cartesian3.fromRadiansArray(polylineCoords),
              width: 1.5,
              material: new Cesium.Color.fromCssColorString('#487BF2'),
              clampToGround: true
            }
          });

          rectangleAdded.current = true;
          dispatch({ type: 'SET_DOWNLOAD_POLYGON', downloadPolygonAdded: true });
          removeMouseHandlers();
        } else {
          window.MAIN_CESIUM_VIEWER.entities.remove(window.itemsPolygon);
          delete (window.itemsPolygon);
          rectanglePositions.current = [];
          rectangleAdded.current = false;
          dispatch({ type: 'SET_DOWNLOAD_POLYGON', downloadPolygonAdded: false });
          rectanglePositions.current.push(getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position));
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  useEffect(() => {
    if (notifyDownloadRef.current && !notifyDownload) {
      dispatch({ type: 'DOWNLOAD_STARTED', downloadStarted: true });
    } else {
      dispatch({ type: 'DOWNLOAD_STARTED', downloadStarted: false });
    }
    notifyDownloadRef.current = notifyDownload;
  }, [notifyDownload])

  const undoArea = () => {
    window.MAIN_CESIUM_VIEWER.entities.remove(window.itemsPolygon);
    delete (window.itemsPolygon);
    rectanglePositions.current = [];
    rectangleAdded.current = false;
    document.getElementById("cesium-wrapper").style.cursor = 'auto';
    dispatch({ type: 'SET_DOWNLOAD_POLYGON', downloadPolygonAdded: false });
  }

  const removeMouseHandlers = () => {
    if (mouseHandler.current) {
      document.getElementById("cesium-wrapper").style.cursor = 'auto';
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
      mouseHandler.current = mouseHandler.current && mouseHandler.current.destroy();
    }
  }

  useEffect(() => {
    removeMouseHandlers();
    setmapboxvisibility(1);
  }, [])

  return (
    <Wrapper>
      <TooltipIconButton title="Close" ariaLabel="close" placement="bottom" onClick={closeModal} size={44} position="true" top={10} right={10}>
        <Close />
      </TooltipIconButton>
      <Content>
        {!downloadPolygonAdded ?
          <TooltipIconButton title="Draw Area" ariaLabel="draw-area" placement="top" size={60} position="true" top={-29} right={30} bluebg="true" onClick={drawArea}>
            <CropOutlined />
          </TooltipIconButton> :
          <TooltipIconButton title="Undo Area" ariaLabel="MarkerEdit" placement="top" size={60} position="true" top={-29} right={30} bluebg="true" onClick={undoArea}>
            <HistoryOutlined />
          </TooltipIconButton>}
        <h3>Data Download</h3>
        <FormItem>
          <p>Catalog</p>
          <FormControl variant="filled" className={classes.formControl}>
            <Select value={currentCollection.value} onChange={(e) => handleChange(e, 'catalog')}>
              {collections.map((item, index) => (
                <MenuItem value={item.value} key={index}>{item.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormItem>
        <FormItem>
          <p>Projection</p>
          <FormControl variant="filled" className={classes.formControl}>
            <Select value={formData.projection} onChange={(e) => handleChange(e, 'projection')}>
              {projections.map((item, index) => (
                <MenuItem value={item.value} key={index}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormItem>
      </Content>
      <Indicators style={{ background: '#2F2F2F' }}>
        <div className="half-section border-right">
          <DataSaverOffOutlined sx={{ color: 'rgb(227 171 84)', fontSize: '24px' }} />
          <p>{totalSize.toFixed(3)}<span>GB</span></p>     
        </div>
        <div className="half-section">
          <CreditCardOutlined sx={{ color: 'rgb(22 198 125)', fontSize: '24px' }}/>
          <p>- <span>$</span></p>
        </div>
      </Indicators>
      <button onClick={downloadSelection} style={{ width: "100%", padding: 0, background: '#2F2F2F', cursor: totalSize === 0 ? 'auto' : 'pointer' }} disabled={totalSize === 0}>
        <Footer>
          {!notifyDownload ?
            !downloadStarted ?
              <>
                <CloudDownloadOutlined sx={{ color: totalSize === 0 ? '#888' : 'white', fontSize: '24px' }}/>
                <span style={{color: totalSize === 0 ? '#888' : 'white'}}>Download Selection</span>
              </> 
              :
              <>
                <CheckCircleOutline sx={{ color: '#15C67D', fontSize: '24px' }}/>
                <span style={{ color: "#15C67D" }}>Download Started</span>
              </>
              :
              <>
                <CircularProgress style={{ color: "#487BF2", width: 24, height: 24 }} />
                <span>Preparing Download</span>
              </>
          }
        </Footer>
      </button>
    </Wrapper>
  );
};

export default DataDownloadModal;